import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Page, Reveal, MaskedLine, Eyebrow, Marquee, EASE } from "@/components/motion/Reveal";
import { IMG } from "@/constants/images";

const SERVICES = [
  {
    n: "01",
    tag: "For the Person",
    title: "Executive Coaching",
    desc: "Helping professionals, executives, entrepreneurs, and emerging leaders gain clarity, strengthen leadership, navigate transitions, build confidence, and intentionally pursue what's next.",
    to: "/executive-coaching",
    cta: "Explore Executive Coaching",
    id: "service-card-executive-coaching",
  },
  {
    n: "02",
    tag: "For the Business",
    title: "Business Development",
    desc: "Helping entrepreneurs and business owners turn vision into strategy, strengthen operations, develop leaders, build resilient teams, and position businesses for sustainable growth.",
    to: "/business-development",
    cta: "Explore Business Development",
    id: "service-card-business-development",
  },
  {
    n: "03",
    tag: "For the Room",
    title: "Motivational Speaking",
    desc: "Inspirational and practical speaking experiences centered on purpose, leadership, resilience, reinvention, entrepreneurship, confidence, and professional growth. For corporate, professional, university, women's leadership, and youth audiences.",
    to: "/speaking",
    cta: "Explore Speaking",
    id: "service-card-motivational-speaking",
  },
];

const EXPERIENCE = [
  "Business Strategy",
  "Multi-Unit Operations",
  "Leadership Development",
  "Organizational Growth",
  "Operational Excellence",
  "Business Performance",
];

const DIFFERENCE = [
  { n: "01", title: "Inspire", desc: "See what is possible. Every next chapter begins with vision." },
  { n: "02", title: "Clarify", desc: "Define where you are going and why it matters." },
  { n: "03", title: "Strategize", desc: "Build the plan, the structure, and the systems." },
  { n: "04", title: "Build", desc: "Take deliberate, accountable action that creates momentum." },
];

const SPEAKING_AUDIENCES = [
  "Corporate Organizations", "Leadership Conferences", "Women's Leadership Events",
  "Universities & Colleges", "Professional Organizations", "Entrepreneurship Events",
  "Emerging Leader Programs", "Youth & Student Organizations", "Schools & Youth Leadership Programs",
];

const TOPICS = [
  "Discover Your Purpose. Develop Your Potential.",
  "Build the Leader Before You Build the Business",
  "From Vision to Execution",
  "Resilient Leadership",
  "Women Who Lead",
];

const YOUTH_TOPICS = [
  "Discover Your Potential",
  "Your Future Starts Now",
  "The Leader in You",
  "The Beginning of What's Next",
];

const MARQUEE_ITEMS = [
  "Purpose", "Leadership", "Growth", "Impact",
  "The Beginning of What's Next", "Inspiration + Clarity + Strategy + Action",
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ringY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <Page data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen bg-obsidian text-ivory overflow-hidden flex items-center">
        <div className="absolute inset-0 hero-glow opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full pt-32 pb-24 grid lg:grid-cols-12 gap-16 items-center relative">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <Eyebrow>Genesis Consulting Firm</Eyebrow>
            </motion.div>
            <h1 className="font-serif font-medium leading-[1.08] tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-8">
              <MaskedLine delay={0.35}>Executive Coaching.</MaskedLine>
              <MaskedLine delay={0.5} className="italic text-gold">Business Development.</MaskedLine>
              <MaskedLine delay={0.65}>Motivational Speaking.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
              className="text-base sm:text-lg font-light leading-relaxed text-ivory/55 mt-8 max-w-xl"
            >
              Helping professionals, entrepreneurs, leaders, and organizations move
              forward with clarity, strategy, and intention.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <Link
                to="/consultation"
                data-testid="hero-cta-book-consultation"
                className="bg-gold text-obsidian px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
              >
                Book a Consultation
              </Link>
              <Link
                to="/speaking"
                data-testid="hero-cta-book-to-speak"
                className="border border-ivory/25 text-ivory px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Book Sharon to Speak
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div style={{ y: ringY }} className="absolute -top-16 -right-10 w-72 h-72 border border-gold/20 rounded-full animate-spin-slow pointer-events-none hidden md:block" />
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
              className="relative"
            >
              <div className="absolute -inset-4 border border-gold/30 translate-x-5 translate-y-5 pointer-events-none" />
              <motion.div style={{ y: photoY }} className="relative overflow-hidden">
                <img
                  src={IMG.founderHero}
                  alt="Sharon Tanyi, Founder & Senior Consultant, Genesis Consulting Firm"
                  data-testid="hero-founder-photo"
                  className="w-full aspect-[4/5] object-cover object-top"
                />
              </motion.div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-obsidian/90 to-transparent p-6 pt-16 translate-x-5 translate-y-5">
                <p className="font-serif text-xl">Sharon Tanyi</p>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold mt-1">
                  Founder & Senior Consultant
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-0 right-0 hidden lg:block"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-between border-t border-white/10 pt-6">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/40">
              The Beginning of What's Next
            </p>
            <ArrowDown size={16} className="text-gold animate-bounce" />
          </div>
        </motion.div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* THE BEGINNING OF WHAT'S NEXT */}
      <section data-testid="philosophy-section" className="bg-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <Reveal><Eyebrow>01 · The Genesis Philosophy</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-obsidian mt-8">
                The Beginning of <span className="italic text-gold-dark">What's Next.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-serif italic text-lg sm:text-xl text-stone-text mt-8 leading-relaxed">
                Discover your purpose. Develop your potential. Build what's next.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <p className="text-base sm:text-lg font-light leading-relaxed text-stone-text">
                Genesis Consulting Firm helps professionals, leaders, entrepreneurs,
                and organizations recognize what's possible, gain clarity about where
                they are going, and create a strategy for moving forward.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mt-10">
                Purpose | Leadership | Growth | Impact
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <p className="text-sm font-light text-stone-text mt-6 leading-relaxed">
                Genesis helps people and organizations move from potential to progress.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THREE WAYS TO WORK WITH GENESIS */}
      <section data-testid="services-section" className="bg-cream py-24 lg:py-36 border-y border-obsidian/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>02 · Three Ways to Work With Genesis</Eyebrow></Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-obsidian/10 border border-obsidian/10 mt-14">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12} className="bg-cream">
                <div data-testid={s.id} className="group flex flex-col p-10 lg:p-12 h-full bg-cream hover:bg-ivory transition-colors duration-500">
                  <span className="font-serif text-5xl text-obsidian/10 group-hover:text-gold/40 transition-colors duration-500">{s.n}</span>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mt-10">{s.tag}</p>
                  <h3 className="font-serif text-2xl text-obsidian mt-3">{s.title}</h3>
                  <p className="text-sm font-light text-stone-text leading-relaxed mt-4 flex-1">{s.desc}</p>
                  <Link
                    to={s.to}
                    data-testid={`${s.id}-explore-button`}
                    className="inline-flex items-center gap-2 mt-8 font-mono text-[10px] tracking-[0.25em] uppercase text-obsidian/70 group-hover:text-gold-dark transition-colors"
                  >
                    {s.cta} <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEET SHARON */}
      <section data-testid="founder-section" className="bg-obsidian text-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 relative">
            <Reveal>
              <div className="absolute -inset-4 border border-gold/25 -translate-x-4 -translate-y-4 pointer-events-none" />
              <img
                src={IMG.founderWarm}
                alt="Sharon Tanyi, Founder of Genesis Consulting Firm"
                data-testid="founder-section-photo"
                className="relative w-full aspect-[4/5] object-cover object-top"
              />
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal><Eyebrow>03 · Meet Sharon</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-8 leading-tight">
                Sharon <span className="italic text-gold">Tanyi</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/40 mt-4">
                Founder & Senior Consultant
              </p>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/40 mt-2">
                · Executive Coach · Entrepreneur · Motivational Speaker
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base sm:text-lg font-light leading-relaxed text-ivory/60 mt-8">
                With more than 20 years of leadership and entrepreneurial experience,
                Sharon Tanyi helps professionals, leaders, entrepreneurs, business
                owners, and organizations see what's possible and build it.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-base font-light leading-relaxed text-ivory/60 mt-5">
                Her background spans organizational leadership, multi-unit operations,
                business ownership, executive leadership, consulting, leadership
                development, organizational growth, operational excellence, and
                business performance.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="text-base font-light leading-relaxed text-ivory/60 mt-5">
                Sharon's mission is to add value to people, equipping them with
                practical tools, guidance, and coaching to help them reach their full
                potential in life.
                From executives and entrepreneurs to women, emerging leaders, college
                students, and youth, Sharon believes potential grows when people are
                given the clarity, confidence, encouragement, and tools to move forward.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <p className="text-base font-light leading-relaxed text-ivory/60 mt-5">
                At the heart of it all, Sharon is a devoted wife and proud mother who
                considers her family her greatest joy. Outside of her professional
                work, she treasures time with her family, especially hiking and baking
                together. She also believes deeply in giving back, serving on local
                boards and volunteering at her church.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <Link
                to="/about"
                data-testid="founder-cta-meet-sharon"
                className="inline-block mt-10 border border-gold/60 text-gold px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
              >
                Meet Sharon
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section data-testid="experience-section" className="bg-ivory py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <Reveal><Eyebrow>Experience</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl sm:text-4xl text-obsidian mt-8 leading-tight">
                  20+ Years of <span className="italic text-gold-dark">Leadership Experience</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-obsidian/10 border border-obsidian/10">
              {EXPERIENCE.map((e, i) => (
                <Reveal key={e} delay={i * 0.05} className="bg-ivory">
                  <div className="p-8 h-full hover:bg-cream transition-colors duration-500" data-testid={`experience-pillar-${i}`}>
                    <span className="font-mono text-[10px] text-gold tracking-[0.3em]">{String(i + 1).padStart(2, "0")}</span>
                    <p className="font-serif text-lg text-obsidian mt-3 leading-snug">{e}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE GENESIS DIFFERENCE */}
      <section data-testid="difference-section" className="bg-cream py-24 lg:py-32 border-y border-obsidian/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>The Genesis Difference</Eyebrow></Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-10">
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-obsidian leading-tight">
                  Inspiration is only <span className="italic text-gold-dark">the beginning.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-base font-light text-stone-text leading-relaxed mt-8">
                  Transformation requires more than motivation. Genesis combines
                  inspiration with clarity, strategy, and action to help people,
                  leaders, and organizations move forward.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-obsidian/10 border border-obsidian/10 self-start">
              {DIFFERENCE.map((d, i) => (
                <Reveal key={d.n} delay={i * 0.08} className="bg-cream">
                  <div className="p-8 h-full" data-testid={`difference-step-${d.n}`}>
                    <span className="font-mono text-[10px] text-gold tracking-[0.3em]">{d.n}</span>
                    <h3 className="font-serif text-2xl text-obsidian mt-3">{d.title}</h3>
                    <p className="text-sm font-light text-stone-text mt-2 leading-relaxed">{d.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.3}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mt-14 text-center">
              Inspire → Clarify → Strategize → Build
            </p>
          </Reveal>
        </div>
      </section>

      {/* FROM STRATEGY TO EXECUTION */}
      <section data-testid="strategy-execution-section" className="bg-ivory py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <Reveal><Eyebrow>From Strategy to Execution</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl text-obsidian mt-8 leading-tight">
                Build the leader. Build the team. <span className="italic text-gold-dark">Build the business.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base sm:text-lg font-light text-stone-text leading-relaxed mt-8">
                Helping business owners and leaders build stronger operations, develop
                high-performing teams, and create organizations positioned for
                sustainable growth.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/business-development"
                data-testid="strategy-execution-cta"
                className="inline-block mt-10 bg-obsidian text-ivory px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
              >
                Explore Business Development
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-3 border border-gold/40 translate-x-3 translate-y-3 pointer-events-none" />
                <img src={IMG.strategy} alt="Strategy session at Genesis Consulting Firm" data-testid="strategy-execution-image" className="relative w-full aspect-[4/5] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SPEAKING PREVIEW */}
      <section data-testid="speaking-preview-section" className="bg-obsidian text-ivory py-24 lg:py-36 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.stage} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/70 to-obsidian" />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative">
          <Reveal><Eyebrow>Speaking</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-8 leading-tight max-w-3xl">
              Inspire the room. <span className="italic text-gold">Move people to action.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-base sm:text-lg font-light text-ivory/60 leading-relaxed mt-8 max-w-2xl">
              Sharon delivers purpose-driven messages that combine inspiration with
              practical insight, helping audiences recognize their potential, strengthen
              their leadership, and take intentional steps toward what's next.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="border border-gold/30 p-8 mt-12 max-w-2xl" data-testid="signature-keynote-card">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-3">Signature Keynote</p>
              <p className="font-serif text-2xl">The Beginning of What's Next</p>
              <p className="text-sm font-light text-ivory/55 mt-3 leading-relaxed">
                A message about purpose, leadership, resilience, reinvention, and
                recognizing when there is more ahead.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 max-w-3xl">
              {TOPICS.map((t, i) => (
                <div key={t} className="flex items-baseline gap-6 py-4 border-b border-white/10" data-testid={`speaking-preview-topic-${i}`}>
                  <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-serif text-lg text-ivory/85">{t}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-ivory/40 mt-10 leading-loose max-w-3xl">
              {SPEAKING_AUDIENCES.join("  ·  ")}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <Link
              to="/speaking"
              data-testid="speaking-preview-cta"
              className="inline-block mt-10 bg-gold text-obsidian px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
            >
              Book Sharon to Speak
            </Link>
          </Reveal>
        </div>
      </section>

      {/* YOUTH & STUDENT SPEAKING */}
      <section data-testid="youth-section" className="bg-navy-deep text-ivory py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <Reveal><Eyebrow>Youth & Student Speaking</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-8 leading-tight">
                Purpose. Potential. Leadership. <span className="italic text-gold">Confidence.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base sm:text-lg font-light text-ivory/60 leading-relaxed mt-8">
                Sharon delivers engaging, age-appropriate messages that encourage young
                people to recognize their potential, develop confidence, discover the
                leader within, and take intentional steps toward their future.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-sm font-light text-ivory/50 leading-relaxed mt-6">
                Speaking experiences can be tailored for schools, student organizations,
                youth leadership programs, college students, and emerging leaders.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <Link
                to="/speaking#inquiry"
                data-testid="youth-cta-inquire"
                className="inline-block mt-10 bg-gold text-obsidian px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
              >
                Inquire About Youth Speaking
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-6">Featured Youth Topics</p>
              <div className="border-t border-white/10">
                {YOUTH_TOPICS.map((t, i) => (
                  <div key={t} className="flex items-baseline gap-6 py-6 border-b border-white/10" data-testid={`youth-topic-${i}`}>
                    <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <p className="font-serif text-xl text-ivory/90">{t}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="font-serif italic text-lg text-gold/90 mt-10">
                From the classroom to the boardroom.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta-section" className="bg-black text-ivory py-28 lg:py-40">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold">
              Ready for What's Next?
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="text-base sm:text-lg font-light text-ivory/55 leading-relaxed mt-8 max-w-2xl mx-auto">
              Whether you're looking for executive coaching, business development
              support, leadership development, or a speaker for your next event,
              let's start the conversation.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="flex flex-wrap justify-center gap-4 mt-12">
              <Link
                to="/consultation"
                data-testid="final-cta-book-consultation"
                className="bg-gold text-obsidian px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
              >
                Book a Consultation
              </Link>
              <Link
                to="/speaking"
                data-testid="final-cta-book-speaking"
                className="border border-gold/50 text-gold px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
              >
                Book Sharon to Speak
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.45}>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-ivory/30 mt-14">
              genesisconsultinghq.com
            </p>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
